/**
 * Engagement template client (task 4170.1): the template catalog, one template's detail, and
 * creating a fully scoped engagement from a template in one request.
 */
import { ApiError, apiFetch } from "../../app/api";

const BASE = "/api/v1/engagement-templates";

export type EnvironmentRole = "production" | "nonproduction" | "simulator";

export interface TemplateSummary {
  template_key: string;
  origin: "builtin" | "custom";
  version: number;
  title: string;
  summary: string;
  engagement_mode: string;
  use_class: string;
  release_mode: "none" | "simulated" | "nonproduction";
  target_domains: string[];
  output_formats: string[];
  uses_ai_provider: boolean;
  sha256: string;
}

export interface ReviewRole {
  responsibility: string;
  role_code: string;
  description: string;
  required: boolean;
}

export interface VerificationStep {
  name: string;
  method: string;
  evidence: string;
}

export interface TemplateDocument {
  template_key: string;
  version: number;
  title: string;
  summary: string;
  engagement_mode: string;
  intended_use: {
    use_key: string;
    title: string;
    statement: string;
    use_class: string;
    permitted_uses: string[];
    prohibited_uses: string[];
  };
  destination: {
    contract_key: string;
    name: string;
    allowed_environment_roles: EnvironmentRole[];
    target_domains: string[];
    target_objects: string[];
    terms: Record<string, unknown>;
  };
  source_population: {
    statement: string;
    bounds: Record<string, unknown>;
    required_bounds: string[];
  };
  trust: { required_trust: string; consequence_of_error: string };
  review_roles: ReviewRole[];
  exclusions: { kind: string; description: string; rationale: string }[];
  stop_conditions: { kind: string; description: string; threshold: unknown }[];
  provider_policy: {
    exposure: Record<string, unknown>;
    budgets: Record<string, unknown>;
    fallback: { allowed: boolean; on_failure: string };
  } | null;
  output_policy: {
    formats: string[];
    release_mode: string;
    require_preflight: boolean;
    notes: string | null;
  };
  verification_plan: { summary: string; steps: VerificationStep[]; acceptance: string };
}

export interface VersionSummary {
  version: number;
  title: string;
  sha256: string;
  change_reason: string | null;
  recorded_at: string | null;
  recorded_by: string | null;
}

export interface TemplateDetail {
  origin: "builtin" | "custom";
  template: TemplateDocument;
  sha256: string;
  versions: VersionSummary[];
  field_map: Record<string, string>;
}

export interface ProviderGrant {
  provider: string;
  models: string[];
  endpoint: string;
  credential_ref: string;
}

export interface CreateFromTemplateRequest {
  reason: string;
  version?: number;
  engagement_key: string;
  name: string;
  environment:
    | { profile_key: string }
    | {
        profile_key: string;
        environment_name: string;
        environment_role: EnvironmentRole;
        description?: string;
      };
  parties: { responsibility: string; party_name: string }[];
  source_bounds: Record<string, unknown>;
  provider?: { grants: ProviderGrant[]; default_provider: string };
}

export interface TemplateApplication {
  id: string;
  engagement_id: string;
  engagement_key: string;
  template_key: string;
  template_version: number;
  template_sha256: string;
  engagement_version: number;
  environment_profile_id: string;
  provider_policy_version: number | null;
  applied_by: string;
  applied_at: string;
}

export interface CreatedEngagement {
  application: TemplateApplication;
  engagement: { id: string; engagement_key: string; lifecycle_state: string };
  scope: { version: number; name: string; required_trust: string };
}

/** Default endpoints of the gateway's provider adapters. */
export const PROVIDER_ENDPOINTS: Record<string, string> = {
  anthropic: "https://api.anthropic.com/v1",
  openai: "https://api.openai.com/v1",
};

export function listTemplates(): Promise<TemplateSummary[]> {
  return apiFetch<TemplateSummary[]>(BASE);
}

export function getTemplate(key: string): Promise<TemplateDetail> {
  return apiFetch<TemplateDetail>(`${BASE}/${encodeURIComponent(key)}`);
}

export function listApplications(): Promise<TemplateApplication[]> {
  return apiFetch<TemplateApplication[]>(`${BASE}/applications?limit=50`);
}

export function createFromTemplate(
  key: string,
  request: CreateFromTemplateRequest,
): Promise<CreatedEngagement> {
  return apiFetch<CreatedEngagement>(`${BASE}/${encodeURIComponent(key)}/engagements`, {
    method: "POST",
    body: JSON.stringify(request),
  });
}

/** A source bound typed by the consultant: comma-separated values become a list. */
export function boundValue(text: string): string | string[] {
  const parts = text
    .split(",")
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
  return parts.length > 1 ? parts : (parts[0] ?? "");
}

/** Every problem the API reported, one per line. */
export function problemsOf(caught: unknown): string[] {
  if (caught instanceof ApiError) {
    const detail = caught.detail;
    if (detail !== null && typeof detail === "object" && "problems" in detail) {
      const problems = (detail as { problems: unknown }).problems;
      if (Array.isArray(problems)) return problems.map(String);
    }
    if (Array.isArray(detail)) {
      return detail.map((item) =>
        item !== null && typeof item === "object" && "msg" in item
          ? String((item as { msg: unknown }).msg)
          : String(item),
      );
    }
    return [caught.message];
  }
  return [caught instanceof Error ? caught.message : String(caught)];
}
